import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { Star, Edit3, Upload as UploadIcon } from 'lucide-react'

interface ProfileData {
  id: string
  handle: string
  displayName: string
  bio?: string
  links: {
    x?: string
    instagram?: string
    discord?: string
    website?: string
  }
  pfpWalrusId?: string
  pfpThumbWalrusId?: string
  bannerWalrusId?: string
  nsfw: boolean
  verified: boolean
  ownerSuiAddress: string
  createdAt: string
}

export default function Profile() {
  const { handle } = useParams<{ handle: string }>()
  const { address, isConnected } = useWallet()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isOwner, setIsOwner] = useState(false)
  const [userContent, setUserContent] = useState<any[]>([])

  useEffect(() => {
    if (handle) {
      fetchProfile(handle)
      fetchUserContent(handle)
    }
  }, [handle])

  useEffect(() => {
    // Check if this is the user's own profile
    if (profile && address) {
      const isOwnProfile = profile.ownerSuiAddress.toLowerCase() === address.toLowerCase()
      console.log('Profile ownership check:', {
        profileAddress: profile.ownerSuiAddress,
        userAddress: address,
        isOwner: isOwnProfile
      })
      setIsOwner(isOwnProfile)
    }
  }, [profile, address])

  const fetchUserContent = async (handle: string) => {
    try {
      // Get content from localStorage
      const allContent = JSON.parse(localStorage.getItem('harmonyContent') || '{}')
      const content = allContent[handle] || []
      setUserContent(content)
      console.log('Loaded user content:', content)
    } catch (error) {
      console.error('Error fetching user content:', error)
      setUserContent([])
    }
  }

  const fetchProfile = async (handle: string) => {
    try {
      setLoading(true)
      
      // First try to get from localStorage (saved profiles)
      const savedProfiles = JSON.parse(localStorage.getItem('harmonyProfiles') || '{}')
      const savedProfile = savedProfiles[handle]
      
      if (savedProfile) {
        console.log('Loading saved profile:', savedProfile)
        // Convert saved profile to ProfileData format
        const profileData: ProfileData = {
          id: savedProfile.handle,
          handle: savedProfile.handle,
          displayName: savedProfile.displayName,
          bio: savedProfile.bio || '',
          links: savedProfile.links || {},
          pfpThumbWalrusId: savedProfile.pfpWalrusId,
          bannerWalrusId: savedProfile.bannerWalrusId,
          nsfw: savedProfile.nsfw || false,
          verified: savedProfile.isVerified || false,
          ownerSuiAddress: savedProfile.walletAddress || '0x0000000000000000000000000000000000000000',
          createdAt: savedProfile.createdAt || new Date().toISOString(),
          // Add the data URLs as additional properties
          pfpDataUrl: savedProfile.pfpDataUrl,
          bannerDataUrl: savedProfile.bannerDataUrl
        } as any
        console.log('Converted profile data:', profileData)
        setProfile(profileData)
        return
      }
      
      // If not found in saved profiles, check if it's a demo profile
      if (handle === 'demo') {
        const demoProfile: ProfileData = {
          id: 'demo',
          handle: 'demo',
          displayName: 'Demo Creator',
          bio: `Welcome to my demo profile! This shows what a creator profile looks like on HarmonyDataHub. I'm sharing amazing content with full ownership and control.`,
          links: {
            x: 'demo_creator',
            instagram: 'demo_creator',
            website: 'https://harmonydatahub.com'
          },
          pfpThumbWalrusId: 'demo-pfp',
          bannerWalrusId: 'demo-banner',
          nsfw: false,
          verified: true, // Demo profile is verified
          ownerSuiAddress: '0x1234567890123456789012345678901234567890',
          createdAt: new Date().toISOString()
        }
        setProfile(demoProfile)
        return
      }
      
      // Profile not found
      setProfile(null)
    } catch (error) {
      console.error('Error fetching profile:', error)
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }

  const getPfpUrl = () => {
    // First check if we have a stored data URL (uploaded image)
    if ((profile as any)?.pfpDataUrl) {
      return (profile as any).pfpDataUrl
    }
    // Then check for Walrus ID (production)
    if (profile?.pfpThumbWalrusId) {
      // In production, this would be: `https://quilt.hdh.ai/${profile.pfpThumbWalrusId}`
      return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
    }
    // Default placeholder
    return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
  }

  const getBannerUrl = () => {
    // First check if we have a stored data URL (uploaded image/video)
    if ((profile as any)?.bannerDataUrl) {
      return (profile as any).bannerDataUrl
    }
    // Then check for Walrus ID (production)
    if (profile?.bannerWalrusId) {
      // In production, this would be: `https://quilt.hdh.ai/${profile.bannerWalrusId}`
      return 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=200&fit=crop'
    }
    // Default placeholder
    return 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=200&fit=crop'
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
          <p className="text-purple-100 text-lg">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="text-center relative z-10">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
              Creator Not Found
            </span>
          </h1>
          <p className="text-purple-100 mb-8 text-lg">This creator doesn't exist or has left the Kingdom.</p>
          <a href="/" className="px-8 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200">
            Return to Kingdom
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Metallic shine effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
      </div>
      
      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-purple-600/80 to-blue-600/80 backdrop-blur-sm overflow-hidden">
        {/* Check if banner is a video or image */}
        {(profile.bannerWalrusId && profile.bannerWalrusId.includes('video')) || ((profile as any)?.bannerDataUrl && (profile as any).bannerDataUrl.startsWith('data:video/')) ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={getBannerUrl()}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${getBannerUrl()})`,
            }}
          />
        )}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-blue-600/30" />
      </div>

      {/* Profile Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-16 relative z-10">
        {/* Profile Picture */}
        <div className="relative">
          <div className="w-32 h-32 rounded-full border-4 border-gold-400/50 bg-white/10 backdrop-blur-sm shadow-2xl overflow-hidden">
            <img
              src={getPfpUrl()}
              alt={`${profile.displayName} profile picture`}
              className="w-full h-full object-cover"
            />
          </div>
          {profile.verified && (
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white/20">
              <Star className="w-4 h-4 text-white fill-current" />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="mt-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Georgia, serif' }}>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
                  {profile.displayName}
                </span>
                {profile.verified && (
                  <span className="text-gold-400" title="Verified Creator">
                    <Star className="w-6 h-6 fill-current" />
                  </span>
                )}
              </h1>
              <p className="text-purple-200 text-lg">@{profile.handle}</p>
            </div>
            
            {/* CTAs */}
            <div className="flex gap-3 flex-wrap">
              {/* Edit Profile Button - Always show for owner */}
              {isOwner && (
                <button
                  onClick={() => navigate('/create-profile?edit=true')}
                  className="px-4 py-2 border border-gold-400/50 text-gold-400 rounded-lg hover:bg-gold-400/10 transition-colors flex items-center gap-2 text-sm"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
              
              {/* Other buttons - Show for everyone or owner */}
              {!isOwner && (
                <button
                  onClick={() => setIsSubscribed(!isSubscribed)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    isSubscribed
                      ? 'bg-white/20 text-white border border-white/30'
                      : 'bg-gold-500 text-black hover:bg-gold-600'
                  }`}
                >
                  {isSubscribed ? 'Subscribed' : 'Subscribe'}
                </button>
              )}
              
              {isOwner && (
                <button
                  onClick={() => navigate('/upload')}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 text-sm"
                >
                  <UploadIcon className="w-4 h-4" />
                  Upload Content
                </button>
              )}
              
              <button
                onClick={() => {}}
                className="px-6 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
              >
                Tip
              </button>
              
              <button className="px-6 py-2 bg-purple-500/20 border border-purple-400/30 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-colors">
                Unlock Content
              </button>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="mb-6">
              <p className="text-purple-100 whitespace-pre-wrap text-lg leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {/* Social Links */}
          <div className="flex gap-4 mb-6">
            {profile.links.website && (
              <a
                href={profile.links.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-gold-400 transition-colors"
                title="Website"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                </svg>
              </a>
            )}
            {profile.links.x && (
              <a
                href={`https://x.com/${profile.links.x}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-blue-400 transition-colors"
                title="X (Twitter)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            )}
            {profile.links.instagram && (
              <a
                href={`https://instagram.com/${profile.links.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-pink-400 transition-colors"
                title="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z"/>
                </svg>
              </a>
            )}
            {profile.links.discord && (
              <a
                href={`https://discord.gg/${profile.links.discord}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-200 hover:text-indigo-400 transition-colors"
                title="Discord"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
            )}
          </div>

          {/* Featured Content Grid */}
          <div className="border-t border-white/20 pt-6">
            <h2 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>Featured Content</h2>
            {userContent.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userContent.map((content) => (
                  <div key={content.id} className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden group hover:bg-white/10 transition-all duration-300">
                    {/* Content Preview */}
                    <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center">
                      {content.file.type.startsWith('image/') ? (
                        <img
                          src={content.fileDataUrl}
                          alt={content.title}
                          className="w-full h-full object-cover"
                        />
                      ) : content.file.type.startsWith('video/') ? (
                        <video
                          src={content.fileDataUrl}
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <div className="text-center">
                          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <svg className="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-purple-300 text-sm">{content.file.name}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Content Info */}
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-2 line-clamp-2">{content.title}</h3>
                      {content.description && (
                        <p className="text-purple-200 text-sm mb-3 line-clamp-2">{content.description}</p>
                      )}
                      
                      {/* Content Meta */}
                      <div className="flex items-center justify-between text-xs text-purple-300">
                        <span className="capitalize">{content.category}</span>
                        <div className="flex items-center gap-2">
                          {content.isPaid && (
                            <span className="bg-gold-500/20 text-gold-400 px-2 py-1 rounded">
                              {content.price} SUI
                            </span>
                          )}
                          {content.isPrivate && (
                            <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                              Private
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2">
                        {content.isPaid && !isOwner ? (
                          <button className="flex-1 bg-gold-500 text-black text-sm py-2 px-3 rounded hover:bg-gold-600 transition-colors">
                            Unlock ({content.price} SUI)
                          </button>
                        ) : (
                          <button className="flex-1 bg-purple-500/20 text-purple-300 text-sm py-2 px-3 rounded hover:bg-purple-500/30 transition-colors">
                            View Content
                          </button>
                        )}
                        {isOwner && (
                          <button className="bg-white/10 text-white text-sm py-2 px-3 rounded hover:bg-white/20 transition-colors">
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-8 text-center">
                <p className="text-purple-200 mb-2 text-lg">No content yet</p>
                <p className="text-sm text-purple-300 mb-4">Content will appear here when published</p>
                {isOwner && (
                  <button
                    onClick={() => navigate('/upload')}
                    className="bg-gold-500 text-black px-6 py-2 rounded-lg hover:bg-gold-600 transition-colors"
                  >
                    Upload Your First Content
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-purple-200 text-sm">
          <p>Powered by <span className="font-semibold text-gold-400">Sui</span> • <span className="font-semibold text-blue-400">Walrus</span> • <span className="font-semibold text-green-400">Seal</span></p>
        </div>
      </div>
    </div>
  )
}
